import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowUpDown,
  ArrowUpRight,
  BadgeCheck,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ClipboardList,
  Clock3,
  CopyPlus,
  FileWarning,
  Image as ImageIcon,
  Link2,
  LoaderCircle,
  MailCheck,
  MailWarning,
  MoreHorizontal,
  PauseCircle,
  Pencil,
  Search,
  Send,
  Sparkles,
  Trash2,
  UsersRound,
  XCircle,
} from "lucide-react";
import CrmColumnHeading from "@/components/CrmColumnHeading";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import SearchableSelect from "@/components/SearchableSelect";
import EventSelect from "@/components/EventSelect";
import { crmHelperTextStyle } from "@/components/crmFormStyles";
import { PhoneInput, LinkedInInput, LINKEDIN_INPUT_HINT, splitStoredPhone, draftPhoneValue, draftLinkedInValue, storedPhoneValue, storedLinkedInValue, linkedinUsernameFromStored } from "@/components/ProfileContactInputs";
import DataTableActions from "@/components/DataTableActions";
import CrmFilterTiles from "@/components/CrmFilterTiles";
import CrmTablePanel from "@/components/CrmTablePanel";
import { formatPhoneForDisplay } from "@shared/profileInputs";
import { adminSpeakerPortraitHref } from "@shared/adminSpeakerPortrait";
import { EVENTS } from "@/lib/eventsData";
import { chiNotify } from "@/components/ChiNotification";
import ResponsiveImage from "@/components/ResponsiveImage";
import { trpc } from "@/lib/trpc";
import { type SpeakerProfileErrors, validateSpeakerProfile } from "@/lib/speakerProfileValidation";
import PersonPortraitAvatar, { CRM_ROW_PORTRAIT_SIZE } from "@/components/PersonPortraitAvatar";
import BiographyAssistant from "@/components/BiographyAssistant";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DISALLOWED_TEXT = /[<>'"]/g;

type SpeakerForm = {
  fullName: string;
  email: string;
  phone: string;
  organization: string;
  professionalTitle: string;
  linkedinUrl: string;
  proposedTopic: string;
  bio: string;
  consentToContact: boolean;
  eventSlugs: string[];
};

type PortraitSource = "application" | "linkedin_image" | "admin_upload";

type PortraitPublicationDialog = {
  id: number;
  memberAccountId?: number;
  sourceKind: string;
  name: string;
  eventSlugs: string[];
  hasApplicationPortrait: boolean;
  isRetry: boolean;
};

const emptyForm: SpeakerForm = {
  fullName: "",
  email: "",
  phone: "",
  organization: "",
  professionalTitle: "",
  linkedinUrl: "",
  proposedTopic: "",
  bio: "",
  consentToContact: false,
  eventSlugs: [],
};

const surface: React.CSSProperties = {
  background: "rgba(13,18,32,0.82)",
  border: "1px solid rgba(255,255,255,0.075)",
  borderRadius: "0.95rem",
};

const fieldInput: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 42,
  border: "1px solid rgba(255,255,255,0.13)",
  borderRadius: "0.58rem",
  background: "rgba(255,255,255,0.045)",
  color: "#F8FAFC",
  padding: "0.68rem 0.75rem",
  outline: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--chi-body-compact)",
};

const statusConfig = {
  approved: { label: "Approved", color: "#86EFAC", background: "rgba(74,222,128,.10)", border: "rgba(74,222,128,.28)" },
  /*
   * Approved, but with no CHI account to sign in with. They have been emailed a
   * link to create one; creating it promotes them to approved automatically.
   */
  waiting_on_account: { label: "Waiting on their account", color: "#7DEAFF", background: "rgba(0,212,255,.10)", border: "rgba(0,212,255,.28)" },
  pending: { label: "Pending review", color: "#FCD34D", background: "rgba(245,158,11,.10)", border: "rgba(245,158,11,.28)" },
  suspended: { label: "Access paused", color: "#FDA4AF", background: "rgba(251,113,133,.10)", border: "rgba(251,113,133,.28)" },
} as const;

function humanizeDate(value: Date | string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function deliveryLabel(status: "pending" | "sent" | "failed" | "not_configured") {
  if (status === "sent") return "Delivered";
  if (status === "pending") return "Queued";
  if (status === "not_configured") return "Not configured";
  return "Needs follow-up";
}

function deliveryColor(status: "pending" | "sent" | "failed" | "not_configured") {
  if (status === "sent") return "#86EFAC";
  if (status === "pending") return "#FCD34D";
  if (status === "not_configured") return "#94A3B8";
  return "#FDA4AF";
}

/**
 * The portrait column's five states, in the words an administrator needs.
 *
 * Every label used to begin with "Portrait", in a column already headed
 * Portrait, so the first word carried nothing and the eye had to read to the
 * third to tell rows apart. These lead with the distinguishing word instead —
 * R / A / P / N / L — and the ones that need action lead with the action.
 *
 * "Live" replaces "Public portrait ready", which was ambiguous: ready to
 * publish, or published? Only this state puts a speaker on the public roster.
 */
function portraitPublicationLabel(status: "not_requested" | "needs_source" | "processing" | "published" | "failed") {
  if (status === "published") return "Published";
  if (status === "processing") return "Processing";
  if (status === "needs_source") return "Add a photo";
  if (status === "failed") return "Retry needed";
  return "Not started";
}

function portraitPublicationColor(status: "not_requested" | "needs_source" | "processing" | "published" | "failed") {
  if (status === "published") return "#86EFAC";
  if (status === "processing") return "#7DEAFF";
  if (status === "needs_source") return "#FCD34D";
  if (status === "failed") return "#FDA4AF";
  return "#94A3B8";
}

/** Column keys the speaker table can be ordered by. */
type SpeakerSortKey = "name" | "status" | "portrait" | "createdAt";

/** Review order: the records needing a decision sort ahead of settled ones. */
const STATUS_RANK: Record<keyof typeof statusConfig, number> = { pending: 0, waiting_on_account: 1, suspended: 2, approved: 3 };

const PORTRAIT_RANK: Record<"not_requested" | "needs_source" | "processing" | "published" | "failed", number> = {
  failed: 0,
  needs_source: 1,
  processing: 2,
  not_requested: 3,
  published: 4,
};

function SortButton({ label, value, active, onSort }: { label: string; value: SpeakerSortKey; active: boolean; onSort: (key: SpeakerSortKey) => void }) {
  return (
    <button type="button" onClick={() => onSort(value)} style={{ display: "inline-flex", alignItems: "center", gap: ".28rem", padding: 0, border: 0, background: "transparent", font: "inherit", letterSpacing: "inherit", textTransform: "inherit", color: active ? "#F9A8D4" : "inherit", cursor: "pointer" }}>
      <CrmColumnHeading label={label} /> <ArrowUpDown size={11} />
    </button>
  );
}

type SpeakerEditField =
  | "fullName" | "phone" | "organization" | "professionalTitle"
  | "linkedinUrl" | "proposedTopic" | "bio";

/** maxLength mirrors the endpoint's bounds so the input stops before a rejection. */
const SPEAKER_EDIT_FIELDS: Array<{ key: SpeakerEditField; label: string; maxLength: number; multiline?: boolean }> = [
  { key: "fullName", label: "Full name", maxLength: 200 },
  { key: "professionalTitle", label: "Professional title", maxLength: 200 },
  { key: "organization", label: "Organization", maxLength: 200 },
  { key: "proposedTopic", label: "Talk topic", maxLength: 500 },
  { key: "bio", label: "Biography", maxLength: 8000, multiline: true },
];

export default function SpeakerCRMView({ memberOpenId, onOpenEvent, onViewSpeakerEvents }: {
  memberOpenId?: string | null;
  onOpenEvent?: (eventSlug: string) => void;
  /** Opens the Events tab narrowed to one speaker. Absent outside the CRM shell. */
  onViewSpeakerEvents?: (speakerName: string) => void;
}) {
  const utils = trpc.useUtils();
  /*
   * The curated roster, read so the context menu can toggle.
   *
   * Featuring is an action on a speaker rather than a separate record to fill in:
   * everything a card needs — name, organisation, headline, biography, portrait,
   * LinkedIn — is already on the speaker being right-clicked.
   */
  const featuredQuery = trpc.speakers.featured.adminList.useQuery(undefined, { refetchOnWindowFocus: false });
  const refreshFeatured = async () => {
    await Promise.all([
      utils.speakers.featured.adminList.invalidate(),
      utils.speakers.featured.publicList.invalidate(),
    ]);
  };
  const setFeatured = trpc.speakers.featured.create.useMutation({
    onSuccess: async () => { await refreshFeatured(); chiNotify.success("Added to Featured Speakers", { description: "It is published on the public Speakers page." }); },
    onError: error => chiNotify.error("Not featured", { description: error.message }),
  });
  const unsetFeatured = trpc.speakers.featured.remove.useMutation({
    onSuccess: async () => { await refreshFeatured(); chiNotify.success("Removed from Featured Speakers"); },
    onError: error => chiNotify.error("Not removed", { description: error.message }),
  });
  /* Matched on the member account when there is one, and on the name otherwise. */
  const featuredFor = (memberAccountId: number | null | undefined, fullName: string) =>
    (featuredQuery.data ?? []).find(row => (
      (memberAccountId != null && row.memberAccountId === memberAccountId)
      || row.displayName.trim().toLocaleLowerCase() === fullName.trim().toLocaleLowerCase()
    )) ?? null;
  const speakersQuery = trpc.speakers.admin.list.useQuery(undefined, { retry: false });
  const pendingRequestsQuery = trpc.speakers.admin.pendingRequests.useQuery(undefined, { retry: false });
  const managedEventsQuery = trpc.events.managedEvents.useQuery(undefined, { retry: false });
  const memberRecordsQuery = trpc.members.adminRecords.useQuery(undefined, {
    enabled: Boolean(memberOpenId),
    retry: false,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [expandedSpeakerId, setExpandedSpeakerId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  /**
   * Which slice of the roster the tiles above it are showing.
   *
   * `all` is the roster proper — everyone who holds the speaker role. `pending`
   * is deliberately not part of it: a pending row is a request to add the role,
   * not a speaker, and it has its own queue above the table.
   */
  const [statusFilter, setStatusFilter] = useState<"all" | "approved" | "waiting_on_account" | "suspended">("all");
  // Alphabetical by speaker, for the same reason the members table is.
  const [sortKey, setSortKey] = useState<SpeakerSortKey>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [form, setForm] = useState<SpeakerForm>(emptyForm);
  /*
   * The display shape of the two formatted create fields.
   *
   * `form` holds what gets sent — E.164 and the full profile URL — while these
   * hold what is on screen: the national number as it is being typed, and the
   * LinkedIn username that sits after the fixed prefix.
   */
  const [createPhone, setCreatePhone] = useState({ countryCode: "+1", national: "" });
  const [createLinkedIn, setCreateLinkedIn] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<SpeakerProfileErrors>({});
  const [speakerToSuspend, setSpeakerToSuspend] = useState<{ id: number; name: string } | null>(null);
  const [speakerToPublish, setSpeakerToPublish] = useState<PortraitPublicationDialog | null>(null);
  const [publicationEventSlugs, setPublicationEventSlugs] = useState<string[]>([]);
  const [portraitSource, setPortraitSource] = useState<PortraitSource>("application");
  const [linkedinImageUrl, setLinkedinImageUrl] = useState("");
    const [uploadDataUrl, setUploadDataUrl] = useState<string | null>(null);
    const [uploadFileName, setUploadFileName] = useState("");
  const [publicationError, setPublicationError] = useState<string | null>(null);
  const [handledMemberOpenId, setHandledMemberOpenId] = useState<string | null>(null);

  const memberPrefill = useMemo(
    () => memberRecordsQuery.data?.find(record => record.userOpenId === memberOpenId) ?? null,
    [memberOpenId, memberRecordsQuery.data],
  );
  const existingSpeakerForMember = useMemo(() => {
    const email = memberPrefill?.email?.trim().toLocaleLowerCase();
    if (!email) return null;
    return speakersQuery.data?.find(({ profile }) => profile.emailNormalized?.trim().toLocaleLowerCase() === email)?.profile ?? null;
  }, [memberPrefill?.email, speakersQuery.data]);
  const assignedTalkTitleBySlug = useMemo(() => {
    const titles = new Map(EVENTS.map(event => [event.slug, event.title]));
    // Managed events are the source of truth for titles edited in Admin. They
    // deliberately overlay the bundled catalog so speakers never see its URL slug.
    (managedEventsQuery.data ?? []).forEach(event => titles.set(event.slug, event.title));
    return titles;
  }, [managedEventsQuery.data]);

  /**
   * Date and time per event slug, so an assignment can be sorted and told apart from
   * one that has already happened.
   *
   * The assignment itself carries only a slug — it is a join row — so "upcoming" is
   * not answerable from it alone. Managed events overlay the bundled catalog for the
   * same reason the titles do: an admin who edited a date should see the edited one.
   */
  const eventTimingBySlug = useMemo(() => {
    const timing = new Map<string, { date: string; displayDate: string; time: string }>(
      EVENTS.map(event => [event.slug, { date: event.date, displayDate: event.displayDate, time: event.time }]),
    );
    for (const event of managedEventsQuery.data ?? []) {
      timing.set(event.slug, { date: event.date, displayDate: event.displayDate, time: event.time });
    }
    return timing;
  }, [managedEventsQuery.data]);

  /**
   * Today as `YYYY-MM-DD`, compared as a string against the event date.
   *
   * Compared by calendar day rather than by instant, so an event running this
   * afternoon is still upcoming — dropping it out on the morning of is the worst
   * possible day to hide it from the person organizing it.
   */
  const todayKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }, []);

  /** The soonest five engagements still to come, in date order. */
  const upcomingAssignments = useCallback((assignments: Array<{ id: number; eventSlug: string }>) => {
    /*
     * flatMap rather than map-then-filter, so the narrowing happens by construction:
     * an assignment whose event has no known timing is dropped here and never has to
     * be re-proven non-null further down.
     */
    const dated = assignments.flatMap(assignment => {
      const timing = eventTimingBySlug.get(assignment.eventSlug);
      if (!timing || timing.date < todayKey) return [];
      return [{ assignment, timing }];
    });

    return dated
      .sort((left, right) => left.timing.date.localeCompare(right.timing.date) || left.timing.time.localeCompare(right.timing.time))
      .slice(0, 5);
  }, [eventTimingBySlug, todayKey]);

  useEffect(() => {
    if (!memberOpenId || handledMemberOpenId === memberOpenId || memberRecordsQuery.isLoading || speakersQuery.isLoading) return;
    if (memberRecordsQuery.isError || !memberPrefill) {
      setHandledMemberOpenId(memberOpenId);
      chiNotify.error("Member record unavailable", { description: "Return to Members and select the person again before creating a speaker profile." });
      return;
    }
    if (!memberPrefill.email) {
      setHandledMemberOpenId(memberOpenId);
      chiNotify.error("Speaker profile needs an email", { description: "Add an email address to this member before creating their speaker profile." });
      return;
    }
    if (existingSpeakerForMember) {
      setSearch(memberPrefill.email);
      setExpandedSpeakerId(existingSpeakerForMember.id);
      setHandledMemberOpenId(memberOpenId);
      return;
    }

    setForm({
      fullName: memberPrefill.name ?? "",
      email: memberPrefill.email,
      phone: memberPrefill.phone ?? "",
      organization: memberPrefill.company ?? "",
      professionalTitle: memberPrefill.professionalTitle ?? "",
      linkedinUrl: memberPrefill.linkedinUrl ?? "",
      proposedTopic: "",
      bio: memberPrefill.bio ?? "",
      consentToContact: false,
      eventSlugs: [],
    });
    setFormError(null);
    setFieldErrors({});
    setShowCreate(true);
    setHandledMemberOpenId(memberOpenId);
  }, [existingSpeakerForMember, handledMemberOpenId, memberOpenId, memberPrefill, memberRecordsQuery.isError, memberRecordsQuery.isLoading, speakersQuery.isLoading]);


  const createSpeaker = trpc.speakers.admin.create.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.speakers.admin.list.invalidate(),
        utils.speakers.admin.pendingRequests.invalidate(),
      ]);
      setForm(emptyForm);
      setCreatePhone({ countryCode: "+1", national: "" });
      setCreateLinkedIn("");
      setFormError(null);
      setFieldErrors({});
      setShowCreate(false);
      chiNotify.success("Speaker request created", { description: "The request is now pending administrator approval. Approval creates or links a free member account; event assignment is optional and separate." });
    },
    onError: error => {
      setFormError(error.message || "The speaker profile could not be created.");
      chiNotify.error("Speaker profile not created", { description: error.message || "Review the required information and try again." });
    },
  });

  /**
   * Full draft of the speaker being edited, so Cancel discards cleanly and the
   * table keeps showing saved values while the drawer is open.
   */
  /** The phone as displayed; the draft itself carries the stored E.164 value. */
  const [editingSpeakerPhone, setEditingSpeakerPhone] = useState<{ countryCode: string; national: string }>({ countryCode: "+1", national: "" });
  const [editingSpeaker, setEditingSpeaker] = useState<null | {
    speakerId: number;
    fullName: string;
    phone: string;
    organization: string;
    professionalTitle: string;
    linkedinUrl: string;
    proposedTopic: string;
    bio: string;
  }>(null);

  const updateSpeakerProfile = trpc.speakers.admin.updateProfile.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      setEditingSpeaker(null);
      chiNotify.success("Speaker updated", { description: "Their profile details have been saved." });
    },
    // Drawer stays open so the draft survives and the reason stays visible.
    onError: error => chiNotify.error("Could not update speaker", { description: error.message }),
  });

  function openSpeakerEditor(profile: {
    id: number;
    fullName: string;
    phone: string | null;
    organization: string | null;
    professionalTitle: string | null;
    linkedinUrl: string | null;
    proposedTopic: string | null;
    bio: string | null;
  }) {
    // The stored number runs back through the display conversion, or the field
    // would open showing raw E.164.
    setEditingSpeakerPhone(splitStoredPhone(profile.phone));
    setEditingSpeaker({
      speakerId: profile.id,
      fullName: profile.fullName ?? "",
      phone: profile.phone ?? "",
      organization: profile.organization ?? "",
      professionalTitle: profile.professionalTitle ?? "",
      linkedinUrl: profile.linkedinUrl ?? "",
      proposedTopic: profile.proposedTopic ?? "",
      bio: profile.bio ?? "",
    });
  }

  const approveSpeaker = trpc.speakers.admin.approve.useMutation({
    onSuccess: async result => {
      await utils.speakers.admin.list.invalidate();
      setSpeakerToPublish(null);
      if (result.publicationOutcome === "published") {
        chiNotify.success("Speaker approved and published", { description: "The standard portrait is ready and the card is now ordered by the assigned event date." });
      } else if (result.publicationOutcome === "needs_source") {
        chiNotify.error("Speaker approved; portrait source needed", { description: "Portal access is enabled, but the public card remains hidden until an authorized portrait source is provided." });
      } else if (result.publicationOutcome === "failed") {
        chiNotify.error("Speaker approved; portrait needs retry", { description: "Portal access is enabled. Review the portrait status and retry from the speaker actions." });
      } else {
        chiNotify.success("Speaker approved", { description: "The speaker may access their portal after signing in with the matching email." });
      }
    },
    onError: error => chiNotify.error("Approval not updated", { description: error.message || "Please try again." }),
  });

  const retryPortraitPublication = trpc.speakers.admin.retryPortraitPublication.useMutation({
    onSuccess: async (result, variables) => {
      await utils.speakers.admin.list.invalidate();
      setSpeakerToPublish(null);
      if (result.outcome === "published") {
        chiNotify.success("Public portrait published", {
          description: variables.mode === "as_is"
            ? "The speaker's own photo is now their public portrait."
            : "The generated speaker card is now available in event-date order.",
        });
      } else if (result.outcome === "needs_source") {
        chiNotify.error("Portrait source needed", { description: "Add a consented application portrait or an authorized direct LinkedIn image URL before retrying." });
      } else {
        chiNotify.error("Portrait processing needs retry", { description: "The request was recorded safely. Review the source image URL and try again." });
      }
    },
    onError: error => chiNotify.error("Portrait processing not started", { description: error.message || "Please try again." }),
  });

  const suspendSpeaker = trpc.speakers.admin.suspend.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker access paused", { description: "The speaker record remains intact and portal access is now blocked." });
    },
    onError: error => chiNotify.error("Access not updated", { description: error.message || "Please try again." }),
  });

  const denyRequest = trpc.speakers.admin.denyRequest.useMutation({
    onSuccess: async result => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker request denied", {
        description: `The request from ${result.fullName} has been removed. Their member account is unaffected.`,
      });
    },
    onError: error => chiNotify.error("Request not denied", { description: error.message || "Please try again." }),
  });

  const approvePendingRequest = trpc.speakers.admin.approveRequest.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.speakers.admin.list.invalidate(),
        utils.speakers.admin.pendingRequests.invalidate(),
      ]);
      chiNotify.success("Speaker permission approved", { description: "A free member account was linked or created. Event assignment can be completed separately." });
    },
    onError: error => chiNotify.error("Speaker request not approved", { description: error.message || "Please try again." }),
  });

  const denyPendingRequest = trpc.speakers.admin.denyPendingRequest.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.pendingRequests.invalidate();
      chiNotify.success("Speaker request denied", { description: "No speaker permission was granted." });
    },
    onError: error => chiNotify.error("Speaker request not denied", { description: error.message || "Please try again." }),
  });

  const removeSpeaker = trpc.speakers.admin.remove.useMutation({
    onSuccess: async result => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker role removed", {
        description: `${result.fullName} is no longer a speaker. Their member account and history remain.`,
      });
    },
    onError: error => chiNotify.error("Speaker not removed", { description: error.message || "Please try again." }),
  });

  /** Awaiting a typed confirmation before it is carried out. */
  const [pendingRemoval, setPendingRemoval] = useState<null | { id: number; name: string; kind: "deny" | "remove" }>(null);

  /** The flyer being viewed full size, if any. */
  const [flyerPreview, setFlyerPreview] = useState<{ url: string; title: string } | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSpeakerId, setAssignSpeakerId] = useState<number | null>(null);
  const [assignEventSlug, setAssignEventSlug] = useState<string | null>(null);

  /**
   * Booking an existing speaker onto an event.
   *
   * Separate from approval: adding a second engagement to an approved speaker
   * should not re-run portrait publication or re-send an approval notice for a
   * decision made weeks ago.
   */
  /**
   * Skip the wait for a speaker to create their account.
   *
   * Named for what it does rather than folded into approve, so the audit trail
   * distinguishes "approved and emailed" from "approved and let straight in".
   */
  const grantPortalAccess = trpc.speakers.admin.grantPortalAccessWithoutAccount.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Portal access granted", { description: "The speaker can sign in without waiting to create an account first." });
    },
    onError: error => chiNotify.error("Access not granted", { description: error.message || "Please try again." }),
  });

  const assignEvent = trpc.speakers.admin.assignEvent.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker assigned", { description: "The talk now appears on the speaker's record." });
    },
    onError: error => chiNotify.error("Speaker not assigned", { description: error.message || "Please try again." }),
  });

  const unassignEvent = trpc.speakers.admin.unassignEvent.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker removed from event", {
        description: "Their speaker profile and other event assignments remain unchanged.",
      });
    },
    onError: error => chiNotify.error("Speaker not removed from event", { description: error.message || "Please try again." }),
  });

  const resendInvitation = trpc.speakers.admin.resendSetupInvitation.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Setup invitation queued", { description: "The status will update after delivery is attempted." });
    },
    onError: error => chiNotify.error("Invitation not queued", { description: error.message || "Please try again." }),
  });


  // The list API can return multiple rows for the same speaker profile (one
  // per assigned event, since profiles and event assignments are joined),
  // so duplicate rows sharing a profile id are folded into one record with
  // their assignments combined before anything counts or renders them.
  const speakerRecords = useMemo(() => {
    const byId = new Map<number, NonNullable<typeof speakersQuery.data>[number]>();
    (speakersQuery.data ?? []).forEach(record => {
      const existing = byId.get(record.profile.id);
      if (existing) {
        byId.set(record.profile.id, {
          profile: { ...existing.profile, ...record.profile },
          assignments: [...existing.assignments, ...record.assignments],
        });
      } else {
        byId.set(record.profile.id, record);
      }
    });
    return Array.from(byId.values());
  }, [speakersQuery.data]);

  /** Any speaker mutation in flight; disables the controls that would race it. */
  const isMutating = approveSpeaker.isPending || approvePendingRequest.isPending || retryPortraitPublication.isPending || suspendSpeaker.isPending || resendInvitation.isPending;

  const pendingCount = useMemo(
    () => pendingRequestsQuery.data?.length ?? 0,
    [pendingRequestsQuery.data],
  );

  /**
   * Speakers who applied through the public form and are still waiting.
   *
   * These arrive from the website's speaker request form, and until now they sat
   * in the main roster indistinguishable from records an administrator created —
   * so a submission could sit unread for as long as it took someone to notice a
   * status chip on row forty. Oldest first: the person who has been waiting
   * longest is the one to answer next.
   */
  const awaitingReview = useMemo(
    () => [...(pendingRequestsQuery.data ?? [])]
      .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()),
    [pendingRequestsQuery.data],
  );

  // Search and ordering happen entirely on the already-authorized payload; no
  // additional records are requested and nothing is sent back to the server.
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...speakerRecords]
      /*
       * Pending rows are requests, not speakers, and they have their own list
       * above. Showing them here too made one queue look like two and left the
       * roster counting people who had not been accepted yet.
       */
      .filter(({ profile }) => profile.status !== "pending")
      .filter(({ profile }) => statusFilter === "all" || profile.status === statusFilter)
      .filter(({ profile }) => {
        if (!needle) return true;
        return [profile.fullName, profile.email, profile.organization, profile.professionalTitle, profile.proposedTopic]
          .some(value => value?.toLowerCase().includes(needle));
      })
      .sort((left, right) => {
        let comparison: number;
        if (sortKey === "name") {
          comparison = (left.profile.fullName ?? "").localeCompare(right.profile.fullName ?? "");
        } else if (sortKey === "status") {
          comparison = STATUS_RANK[left.profile.status] - STATUS_RANK[right.profile.status];
        } else if (sortKey === "portrait") {
          comparison = PORTRAIT_RANK[left.profile.portraitPublicationStatus] - PORTRAIT_RANK[right.profile.portraitPublicationStatus];
        } else {
          comparison = new Date(left.profile.createdAt ?? 0).getTime() - new Date(right.profile.createdAt ?? 0).getTime();
        }
        return sortDirection === "asc" ? comparison : -comparison;
      });
  }, [search, sortDirection, sortKey, speakerRecords, statusFilter]);

  function changeSort(next: SpeakerSortKey) {
    if (next === sortKey) {
      setSortDirection(direction => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(next);
    setSortDirection(next === "name" ? "asc" : "desc");
  }

  function updateText(key: Exclude<keyof SpeakerForm, "consentToContact" | "eventSlugs">, value: string) {
    setForm(current => ({ ...current, [key]: value.replace(DISALLOWED_TEXT, "") }));
    setFieldErrors(current => {
      if (!current[key]) return current;
      const { [key]: _cleared, ...remaining } = current;
      return remaining;
    });
  }

  function toggleEvent(eventSlug: string) {
    setForm(current => ({
      ...current,
      eventSlugs: current.eventSlugs.includes(eventSlug)
        ? current.eventSlugs.filter(slug => slug !== eventSlug)
        : [...current.eventSlugs, eventSlug],
    }));
  }

  function openPortraitPublication(
    profile: { id: number; memberAccountId?: number; sourceKind: string; fullName: string; portraitConsent: number; portraitStorageKey: string | null },
    assignments: Array<{ eventSlug: string }>,
    isRetry = false,
  ) {
    const hasApplicationPortrait = profile.portraitConsent === 1 && Boolean(profile.portraitStorageKey);
    setPublicationEventSlugs(assignments.map(assignment => assignment.eventSlug));
    setPortraitSource(
      hasApplicationPortrait
        ? "application"
        : profile.sourceKind === "member_permission"
          ? "admin_upload"
          : "linkedin_image",
    );
    setLinkedinImageUrl("");
        setUploadDataUrl(null);
        setUploadFileName("");
    setPublicationError(null);
    setSpeakerToPublish({
      id: profile.id,
      memberAccountId: profile.memberAccountId,
      sourceKind: profile.sourceKind,
      name: profile.fullName,
      eventSlugs: assignments.map(assignment => assignment.eventSlug),
      hasApplicationPortrait,
      isRetry,
    });
  }

  /*
   * Publishes the speaker's existing photo as their portrait, unstyled. No dialog:
   * there is nothing to choose — the photo is the one already on file — and this is
   * the action an administrator reaches for when "Retry" has stopped meaning
   * anything because the image service is not there to retry against.
   */
  /** Whether there is a photo on file to publish: the member's own, or one an administrator supplied. */
  function hasPortraitSource(profile: { portraitStorageKey: string | null; portraitPublicationSourceStorageKey?: string | null }) {
    return Boolean(profile.portraitStorageKey) || Boolean(profile.portraitPublicationSourceStorageKey);
  }

  function publishPhotoAsIs(profile: { id: number; memberAccountId?: number; sourceKind: string }) {
    retryPortraitPublication.mutate(
      profile.sourceKind === "member_permission" && profile.memberAccountId
        ? { memberAccountId: profile.memberAccountId, mode: "as_is" }
        : { speakerId: profile.id, mode: "as_is" },
    );
  }

  function openPortraitUpload(
    profile: { id: number; memberAccountId?: number; sourceKind: string; fullName: string; portraitConsent: number; portraitStorageKey: string | null },
    assignments: Array<{ eventSlug: string }>,
  ) {
    openPortraitPublication(profile, assignments, true);
    setPortraitSource("admin_upload");
  }

  function togglePublicationEvent(eventSlug: string) {
    setPublicationEventSlugs(current => (
      current.includes(eventSlug)
        ? current.filter(slug => slug !== eventSlug)
        : [...current, eventSlug]
    ));
    setPublicationError(null);
  }

  function submitPortraitPublication(event: React.FormEvent) {
    event.preventDefault();
    if (!speakerToPublish) return;
    if (
      speakerToPublish.isRetry &&
      speakerToPublish.sourceKind === "member_permission" &&
      speakerToPublish.memberAccountId &&
      portraitSource !== "admin_upload"
    ) {
      retryPortraitPublication.mutate({ memberAccountId: speakerToPublish.memberAccountId });
      return;
    }
    if (!speakerToPublish.isRetry && publicationEventSlugs.length === 0) {
      setPublicationError("Assign at least one canonical event before approving this speaker for the public roster.");
      return;
    }
    if (portraitSource === "application" && !speakerToPublish.hasApplicationPortrait) {
      setPublicationError("This speaker has no consented application portrait. Select the authorized LinkedIn image option instead.");
      return;
    }
    if (portraitSource === "linkedin_image" && !linkedinImageUrl.trim()) {
      setPublicationError("Enter an authorized direct LinkedIn image URL. The public LinkedIn profile URL alone cannot be processed.");
      return;
    }
        if (portraitSource === "admin_upload" && !uploadDataUrl) {
                setPublicationError("Choose a photo file to upload.");
                return;
        }

    const portraitInput = {
            portraitSource,
            linkedinImageUrl: portraitSource === "linkedin_image" ? linkedinImageUrl.trim() : undefined,
            upload: portraitSource === "admin_upload" && uploadDataUrl
                      ? { dataUrl: uploadDataUrl, fileName: uploadFileName || "speaker-portrait" }
                      : undefined,
    } as const;
    if (speakerToPublish.isRetry) {
      if (speakerToPublish.sourceKind === "member_permission" && speakerToPublish.memberAccountId) {
        /*
         * With the source input, like the legacy branch below. Without it the
         * uploaded photo never left the browser: the server fell back to the
         * member's (absent) profile photo and answered "Add a photo" to an
         * administrator who had just added one.
         */
        retryPortraitPublication.mutate({ memberAccountId: speakerToPublish.memberAccountId, ...portraitInput });
      } else {
        retryPortraitPublication.mutate({ speakerId: speakerToPublish.id, ...portraitInput });
      }
    } else {
      approveSpeaker.mutate({
        speakerId: speakerToPublish.id,
        eventSlugs: publicationEventSlugs,
        ...portraitInput,
      });
    }
  }

    const MAX_PORTRAIT_UPLOAD_BYTES = 1.5 * 1024 * 1024;
    const ALLOWED_PORTRAIT_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp"];

    function handlePortraitFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
          const file = event.target.files?.[0];
          if (!file) return;
          if (!ALLOWED_PORTRAIT_UPLOAD_TYPES.includes(file.type)) {
                  setPublicationError("Upload a PNG, JPEG, or WebP portrait.");
                  event.target.value = "";
                  return;
          }
          if (file.size > MAX_PORTRAIT_UPLOAD_BYTES) {
                  setPublicationError("Speaker portraits must be 1.5 MB or smaller.");
                  event.target.value = "";
                  return;
          }
          const reader = new FileReader();
          reader.onload = () => {
                  setUploadDataUrl(typeof reader.result === "string" ? reader.result : null);
                  setUploadFileName(file.name);
                  setPublicationError(null);
          };
          reader.onerror = () => setPublicationError("The selected file could not be read. Try a different photo.");
          reader.readAsDataURL(file);
    }

  function submitCreate(event: React.FormEvent) {
    event.preventDefault();
    const errors = validateSpeakerProfile(form);
    const firstInvalidField = Object.keys(errors)[0] as keyof SpeakerProfileErrors | undefined;
    if (firstInvalidField) {
      setFieldErrors(errors);
      setFormError("Review the highlighted fields and correct the information before creating this profile.");
      const firstControlId = firstInvalidField === "consentToContact"
        ? "speaker-create-consent"
        : `speaker-create-${firstInvalidField}`;
      requestAnimationFrame(() => document.getElementById(firstControlId)?.focus());
      return;
    }
    setFormError(null);
    setFieldErrors({});
    createSpeaker.mutate({
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      organization: form.organization.trim(),
      professionalTitle: form.professionalTitle.trim(),
      linkedinUrl: form.linkedinUrl.trim(),
      proposedTopic: form.proposedTopic.trim(),
      bio: form.bio.trim(),
      consentToContact: form.consentToContact,
      eventSlugs: form.eventSlugs,
    });
  }


  if (speakersQuery.isLoading) {
    return <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#94A3B8" }}><LoaderCircle className="animate-spin" aria-label="Loading speaker profiles" /></div>;
  }

  if (speakersQuery.isError) {
    return (
      <div style={{ flex: 1, display: "grid", placeItems: "center", padding: "2rem" }}>
        <div style={{ ...surface, maxWidth: 540, padding: "2rem", textAlign: "center" }}>
          <FileWarning size={32} style={{ color: "#FDA4AF", marginBottom: "0.8rem" }} />
          <h2 style={{ margin: 0, color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem" }}>Speaker records are unavailable</h2>
          <p style={{ margin: "0.7rem 0 1.25rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.65 }}>The secure speaker queue could not be loaded. No changes have been made.</p>
          <button type="button" onClick={() => speakersQuery.refetch()} style={{ minHeight: 42, padding: "0.7rem 0.9rem", borderRadius: "0.6rem", border: "1px solid rgba(233,30,140,.45)", background: "rgba(233,30,140,.12)", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: "pointer" }}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(1.25rem, 3vw, 2.5rem)", width: "100%", boxSizing: "border-box" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        <div>
          <p style={{ margin: "0 0 .35rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".12em", textTransform: "uppercase" }}>Admin CRM · Secure speaker lifecycle</p>
          <h1 style={{ margin: 0, color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3.1rem)", letterSpacing: ".025em", lineHeight: 1 }}>Speakers</h1>
          <p style={{ margin: ".65rem 0 0", color: "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6, maxWidth: 700 }}>Create approved-ready records, optionally assign talks, review public applications, and monitor non-secret delivery status. Speaker access remains approval-gated and event data remains read-only for speakers.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => { setAssignOpen(true); setAssignSpeakerId(null); setAssignEventSlug(null); }}
            style={{ minHeight: 44, display: "inline-flex", alignItems: "center", gap: ".5rem", padding: ".7rem .9rem", border: "1px solid rgba(0,212,255,.4)", borderRadius: ".65rem", background: "rgba(0,212,255,.09)", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}
          >
            <CalendarPlus size={16} /> Assign speaker to event
          </button>
        </div>
      </header>

      {awaitingReview.length > 0 && (
        /*
          Only rendered when there is something to answer. A permanently visible
          empty queue trains people to stop looking at it.
        */
        <section data-speaker-review-queue aria-labelledby="speaker-review-queue" style={{ ...surface, marginBottom: "1.25rem", borderColor: "rgba(245,166,35,.34)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", padding: "1rem 1.1rem", borderBottom: "1px solid rgba(245,166,35,.22)", background: "rgba(245,166,35,.07)" }}>
            <div>
              <h2 id="speaker-review-queue" style={{ margin: 0, color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1rem", fontWeight: 700 }}>
                {awaitingReview.length} speaker request{awaitingReview.length === 1 ? "" : "s"} awaiting review
              </h2>
              <p style={{ margin: ".2rem 0 0", color: "#C6B08A", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                Submitted through the speaker request form on the website. Oldest first.
              </p>
            </div>
            <ClipboardList size={20} color="#F5A623" aria-hidden="true" />
          </div>

          <div className="speaker-table-wrap">
            <table className="speaker-table">
              <caption className="sr-only">Speaker requests submitted from the website, awaiting an approval decision.</caption>
              <thead>
                <tr>
                  <th scope="col"><CrmColumnHeading label="Speaker" /></th>
                  <th scope="col"><CrmColumnHeading label="Proposed topic" /></th>
                  <th scope="col"><CrmColumnHeading label="Requested" /></th>
                  <th scope="col"><CrmColumnHeading label="Decision" /></th>
                </tr>
              </thead>
              <tbody>
                {awaitingReview.map(request => (
                  <tr key={`review-${request.id}`}>
                    <td>
                      <strong style={{ display: "block", color: "#F4F7FA", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{request.fullName}</strong>
                      <span style={{ color: "#7E8CA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{request.organization || request.email || "No organisation given"}</span>
                    </td>
                    <td style={{ color: "#CFD8E6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>{request.proposedTopic || "No topic given"}</td>
                    <td style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", whiteSpace: "nowrap" }}>{humanizeDate(request.createdAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="speaker-crm-detail__cta"
                          style={{ margin: 0 }}
                          disabled={isMutating}
                          onClick={() => approvePendingRequest.mutate({ requestId: request.id })}
                        >
                          <BadgeCheck size={13} aria-hidden="true" /> Approve
                        </button>
                        {/* Deny deletes the request. The member account behind it
                            stays — they asked to speak and were declined, which is
                            not a reason to remove them from the community. */}
                        <button
                          type="button"
                          className="speaker-crm-review__secondary"
                          disabled={denyPendingRequest.isPending}
                          onClick={() => denyPendingRequest.mutate({ requestId: request.id })}
                          style={{ color: "#FDA4AF", borderColor: "rgba(251,113,133,.4)" }}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/*
        The same tiles the Members and Payments tables use, and clickable for the
        same reason: a count that cannot be clicked makes an administrator find
        the matching filter somewhere else on the page.

        "Pending" is a count only — those rows live in the review queue above,
        not in this table — so it scrolls to that queue rather than filtering a
        table it is deliberately excluded from.
      */}
      <CrmFilterTiles
        ariaLabel="Speaker filters"
        tiles={[
          { key: "all", label: "Total speakers", value: speakerRecords.filter(item => item.profile.status !== "pending").length, icon: UsersRound,
            color: "#00D4FF", background: "rgba(0,212,255,.10)", border: "rgba(0,212,255,.24)",
            selected: statusFilter === "all", onSelect: () => setStatusFilter("all") },
          { key: "approved", label: "Approved", value: speakerRecords.filter(item => item.profile.status === "approved").length, icon: CheckCircle2,
            color: "#86EFAC", background: "rgba(74,222,128,.10)", border: "rgba(74,222,128,.28)",
            selected: statusFilter === "approved", onSelect: () => setStatusFilter(statusFilter === "approved" ? "all" : "approved") },
          { key: "suspended", label: "Suspended", value: speakerRecords.filter(item => item.profile.status === "suspended").length, icon: XCircle,
            color: "#FCA5A5", background: "rgba(248,113,113,.10)", border: "rgba(248,113,113,.28)",
            selected: statusFilter === "suspended", onSelect: () => setStatusFilter(statusFilter === "suspended" ? "all" : "suspended") },
          { key: "pending", label: "Pending review", value: pendingCount, icon: Clock3,
            color: "#FCD34D", background: "rgba(245,158,11,.10)", border: "rgba(245,158,11,.28)",
            title: "Requests to add the speaker role — reviewed in the queue above this table",
            selected: false,
            onSelect: () => document.querySelector("[data-speaker-review-queue]")?.scrollIntoView({ behavior: "smooth", block: "start" }) },
        ]}
      />

      {/*
        Creating a speaker happens in a drawer rather than inline.
        The form used to expand between the page header and the roster, pushing the
        table down: the admin lost sight of the list they were adding to, and the
        submit button on a form this long ended up below the fold. A sheet keeps the
        table in place, dims it to signal a modal task, and gives Escape and the
        close button an unambiguous meaning.
      */}
      {/*
        Edit drawer. Same pattern as create: the roster stays put behind it and
        Escape closes without saving. Email is absent on purpose -- it is the
        passwordless sign-in identity, so changing it here would detach the speaker
        from their own account.
      */}
      <Sheet open={Boolean(editingSpeaker)} onOpenChange={open => { if (!open) setEditingSpeaker(null); }}>
        <SheetContent side="right" className="speaker-create-drawer">
          {/* The shared CRM drawer skin, so this reads the same as the member
              edit drawer and the assign drawer rather than being a third look. */}
          <form
            className="crm-drawer"
            onSubmit={event => { event.preventDefault(); if (editingSpeaker) updateSpeakerProfile.mutate(editingSpeaker); }}
          >
            <SheetHeader className="crm-drawer__header">
              <SheetTitle className="crm-drawer__title">Edit speaker profile</SheetTitle>
              <SheetDescription className="crm-drawer__subtitle">
                Approval status, event assignments and the sign-in email are managed elsewhere.
              </SheetDescription>
            </SheetHeader>

            <div className="crm-drawer__body">
              {SPEAKER_EDIT_FIELDS.map(field => (
                <label key={field.key} style={{ display: "grid", gap: ".3rem" }}>
                  <span className="crm-drawer__label" style={{ marginTop: 0 }}>{field.label}</span>
                  {field.multiline ? (
                    <textarea
                      value={editingSpeaker?.[field.key] ?? ""}
                      rows={7}
                      maxLength={field.maxLength}
                      onChange={e => setEditingSpeaker(current => current && { ...current, [field.key]: e.target.value })}
                      style={{ ...fieldInput, minHeight: 150, resize: "vertical", lineHeight: 1.55 }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={editingSpeaker?.[field.key] ?? ""}
                      maxLength={field.maxLength}
                      onChange={e => setEditingSpeaker(current => current && { ...current, [field.key]: e.target.value })}
                      style={fieldInput}
                    />
                  )}
                </label>
              ))}

              {/*
                The same LinkedIn control the rest of the product uses: a username
                after a fixed prefix, stored as the full profile URL. This field
                was free text, so an admin could save something that was not a
                LinkedIn URL at all.
              */}
              {/* Formats as you type, stores E.164 — the create-account behaviour. */}
              <span className="crm-drawer__label">Phone</span>
              <PhoneInput
                countryCode={editingSpeakerPhone.countryCode}
                value={editingSpeakerPhone.national}
                onChange={next => {
                  setEditingSpeakerPhone(next.countryCode === editingSpeakerPhone.countryCode
                    ? { ...editingSpeakerPhone, national: next.value }
                    : { countryCode: next.countryCode, national: next.value });
                  setEditingSpeaker(current => current && {
                    ...current,
                    // Runs per keystroke; an incomplete number is simply not stored yet.
                    phone: draftPhoneValue(next.countryCode, next.value) ?? "",
                  });
                }}
              />

              <span className="crm-drawer__label">LinkedIn</span>
              <LinkedInInput
                value={linkedinUsernameFromStored(editingSpeaker?.linkedinUrl ?? "")}
                onChange={username => setEditingSpeaker(current => current && { ...current, linkedinUrl: username })}
              />
              <span style={crmHelperTextStyle}>{LINKEDIN_INPUT_HINT}</span>
            </div>

            <div className="crm-drawer__footer">
              <button type="button" className="crm-drawer__secondary" onClick={() => setEditingSpeaker(null)}>Discard</button>
              <button type="submit" className="crm-drawer__primary" disabled={updateSpeakerProfile.isPending} style={{ display: "inline-flex", alignItems: "center", gap: ".45rem" }}>
                {updateSpeakerProfile.isPending ? <><LoaderCircle className="animate-spin" size={14} /> Saving…</> : "Save changes"}
              </button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/*
        Booking an existing speaker onto a talk. Both pickers filter as you type
        because both lists outgrow a plain dropdown: the roster keeps growing and
        the calendar already runs to dozens of events.
      */}
      {flyerPreview && (
        /*
          A plain overlay rather than a Sheet: this is one image with nothing to
          fill in, and a drawer would crop a portrait flyer to a column.
        */
        <div
          className="speaker-flyer-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Flyer for ${flyerPreview.title}`}
          onClick={() => setFlyerPreview(null)}
        >
          <div className="speaker-flyer-lightbox__panel" onClick={event => event.stopPropagation()}>
            <div className="speaker-flyer-lightbox__bar">
              <span>{flyerPreview.title}</span>
              <div style={{ display: "flex", gap: ".5rem" }}>
                {onOpenEvent && (
                  <button type="button" className="speaker-crm-review__secondary" onClick={() => setFlyerPreview(null)}>Close</button>
                )}
                <a className="speaker-crm-review__secondary" href={flyerPreview.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  Open full size
                </a>
              </div>
            </div>
            <ResponsiveImage
              src={flyerPreview.url}
              alt={`Promotional flyer for ${flyerPreview.title}`}
              sizes="(max-width: 900px) 100vw, 720px"
            />
          </div>
        </div>
      )}

      <Sheet open={assignOpen} onOpenChange={setAssignOpen}>
        <SheetContent side="right" className="speaker-create-drawer">
          <div className="crm-drawer">
            <SheetHeader className="crm-drawer__header">
              <SheetTitle className="crm-drawer__title">Manage an event’s speakers</SheetTitle>
              <SheetDescription className="crm-drawer__subtitle">
                Choose an event to see every approved speaker. Checked speakers appear on that
                event; unchecking removes only that booking.
              </SheetDescription>
            </SheetHeader>

            <div className="crm-drawer__body">
              <label className="crm-drawer__label" htmlFor="assign-event">Event</label>
              <EventSelect id="assign-event" value={assignEventSlug} onChange={setAssignEventSlug} />

              {assignEventSlug && (
                <fieldset style={{ border: 0, padding: 0, margin: "1.25rem 0 0", display: "grid", gap: ".55rem" }}>
                  <legend className="crm-drawer__label" style={{ marginBottom: ".6rem" }}>
                    Assigned speakers
                  </legend>
                  {[...speakerRecords]
                    .filter(record => record.profile.status === "approved")
                    .sort((a, b) => (
                      Number(b.profile.id === assignSpeakerId) - Number(a.profile.id === assignSpeakerId)
                      || a.profile.fullName.localeCompare(b.profile.fullName)
                    ))
                    .map(record => {
                      const checked = record.assignments.some(assignment => assignment.eventSlug === assignEventSlug);
                      const pending = (
                        (assignEvent.isPending && assignEvent.variables?.speakerId === record.profile.id)
                        || (unassignEvent.isPending && unassignEvent.variables?.speakerId === record.profile.id)
                      );
                      return (
                        <label
                          key={record.profile.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: ".75rem",
                            minHeight: 52,
                            padding: ".7rem .8rem",
                            border: `1px solid ${checked ? "rgba(125,234,255,.34)" : "rgba(255,255,255,.09)"}`,
                            borderRadius: ".7rem",
                            background: checked ? "rgba(125,234,255,.07)" : "rgba(255,255,255,.025)",
                            cursor: pending ? "wait" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={pending || assignEvent.isPending || unassignEvent.isPending}
                            onChange={event => {
                              const input = { speakerId: record.profile.id, eventSlug: assignEventSlug };
                              if (event.target.checked) assignEvent.mutate(input);
                              else unassignEvent.mutate(input);
                            }}
                            aria-label={`${checked ? "Remove" : "Assign"} ${record.profile.fullName} ${checked ? "from" : "to"} this event`}
                            style={{ width: 18, height: 18, accentColor: "#7DEAFF", flexShrink: 0 }}
                          />
                          <span style={{ minWidth: 0 }}>
                            <strong style={{ display: "block", color: "#F8FAFC", fontSize: "var(--chi-body-compact)" }}>
                              {record.profile.fullName}
                            </strong>
                            <span style={{ display: "block", marginTop: ".15rem", color: "#8290A5", fontFamily: "'Inter', sans-serif", fontSize: "calc(var(--chi-body-compact) * .9)", overflowWrap: "anywhere" }}>
                              {[record.profile.professionalTitle, record.profile.organization].filter(Boolean).join(" · ") || "Approved speaker"}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                </fieldset>
              )}
            </div>

            <div className="crm-drawer__footer">
              <button
                type="button"
                className="crm-drawer__secondary"
                onClick={() => setAssignOpen(false)}
              >
                Cancel
              </button>
              <span style={{ color: "#8290A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                {assignEventSlug
                  ? `${speakerRecords.filter(record => record.assignments.some(assignment => assignment.eventSlug === assignEventSlug)).length} assigned`
                  : "Choose an event to manage its roster"}
              </span>
            </div>
          </div>
        </SheetContent>
      </Sheet>


      <CrmTablePanel
        title="Speaker roster"
        description="Right-click a speaker for actions, or use its Actions button. Profile details and delivery statuses are visible only to administrators."
        icon={UsersRound}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search speaker profiles"
        searchPlaceholder="Search name, email, organization, title or topic…"
        shownCount={rows.length}
        totalCount={speakerRecords.length}
        noun="profile"
        emptyState={
          <div style={{ padding: "2.5rem 1.25rem", textAlign: "center" }}><UsersRound size={30} style={{ color: "#475569", marginBottom: ".8rem" }} /><p style={{ margin: 0, color: "#CCD6E5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 650 }}>No speaker profiles yet</p><p style={{ margin: ".4rem 0 0", color: "#728096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>Create a speaker record or wait for a public speaker application to appear here.</p></div>
        }
      >
        <>
            <div className="speaker-table-wrap">
              <table className="speaker-table" aria-label="Speaker roster">
                <caption className="sr-only">Speaker profiles with lifecycle status, public portrait state, assigned events, and administrator actions.</caption>
                <thead>
                  <tr>
                    {/* Unlabelled: a face needs no heading, and naming it would compete with Speaker. */}
                    <th scope="col" className="speaker-portrait-cell"><span className="sr-only">Portrait</span></th>
                    <th scope="col"><SortButton label="Speaker" value="name" active={sortKey === "name"} onSort={changeSort} /></th>
                    <th scope="col"><CrmColumnHeading label="Organization / title" /></th>
                    <th scope="col"><SortButton label="Status" value="status" active={sortKey === "status"} onSort={changeSort} /></th>
                    <th scope="col"><SortButton label="Portrait" value="portrait" active={sortKey === "portrait"} onSort={changeSort} /></th>
                    <th scope="col"><SortButton label="Created" value="createdAt" active={sortKey === "createdAt"} onSort={changeSort} /></th>
                    <th scope="col"><CrmColumnHeading label="Actions" /></th>
                  </tr>
                </thead>
                <tbody>
            {rows.map(({ profile, assignments }) => {
              const state = statusConfig[profile.status];
              const isExpanded = expandedSpeakerId === profile.id;
              const lifecycleActionLabel = profile.status === "approved" ? "Remove Speaker Access" : profile.status === "suspended" ? "Restore Speaker Access" : "Grant Speaker Access";
              const toggleSpeakerDetails = () => setExpandedSpeakerId(current => current === profile.id ? null : profile.id);
          // The published portrait is the one the public sees, so it wins.
          // Before publication the reviewer would otherwise face initials
          // while deciding whether to approve a portrait, so the consented
          // application image stands in, read through the admin-only route,
          // never from the public proxy, which refuses this prefix.
          const rowPortraitUrl = profile.crmPortraitUrl
            ?? (profile.portraitConsent === 1 && profile.portraitStorageKey
              ? adminSpeakerPortraitHref(profile.id)
              : null);
              const portraitNeedsAttention = !rowPortraitUrl && profile.portraitPublicationStatus !== "processing";
              return <ContextMenu key={profile.id}>
                <ContextMenuTrigger asChild>
                  <tr
                    className={`speaker-crm-row${isExpanded ? " speaker-crm-row--expanded" : ""}`}
                    tabIndex={0}
                    aria-label={`${isExpanded ? "Hide" : "Show"} details for ${profile.fullName}`}
                    onClick={toggleSpeakerDetails}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleSpeakerDetails();
                      }
                    }}
                  >
                  {/*
                    The portrait's own cell, under the table's unlabelled first
                    heading — the same pattern as the members and events tables, so
                    every other column sits under the heading that names it rather
                    than being shifted right by a face inside cell one.
                  */}
                  <td className="speaker-portrait-cell">
                    <div className="speaker-crm-portrait-wrap">
                      <PersonPortraitAvatar name={profile.fullName} portraitUrl={rowPortraitUrl} size={CRM_ROW_PORTRAIT_SIZE} accent={state.color} />
                      {portraitNeedsAttention && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="speaker-crm-portrait-alert"
                              aria-label={`Portrait error for ${profile.fullName}`}
                              title="Portrait error — open actions"
                              onClick={event => event.stopPropagation()}
                              onKeyDown={event => event.stopPropagation()}
                            >
                              <span aria-hidden="true">!</span>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            sideOffset={6}
                            onClick={event => event.stopPropagation()}
                            style={{ width: 292, padding: ".45rem", background: "#111A2B", borderColor: "rgba(248,113,113,.36)" }}
                          >
                            <DropdownMenuLabel style={{ padding: ".45rem .55rem .2rem", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}>
                              Portrait needs attention
                            </DropdownMenuLabel>
                            <p style={{ margin: "0 .55rem .5rem", color: "#AAB5C8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>
                              There was an error while generating this speaker&apos;s profile picture.
                            </p>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={isMutating}
                              onSelect={() => openPortraitPublication(profile, assignments, true)}
                            >
                              <BadgeCheck size={14} aria-hidden="true" /> Retry portrait generation
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={isMutating}
                              onSelect={() => openPortraitUpload(profile, assignments)}
                            >
                              <ImageIcon size={14} aria-hidden="true" /> Provide a source photo
                            </DropdownMenuItem>
                            {hasPortraitSource(profile) && (
                              <DropdownMenuItem
                                disabled={isMutating}
                                onSelect={() => publishPhotoAsIs(profile)}
                              >
                                <BadgeCheck size={14} aria-hidden="true" /> Use current photo as portrait
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </td>
                  <td>
                  <div style={{ display: "flex", alignItems: "center", gap: ".65rem", minWidth: 0, textAlign: "left" }}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#F4F7FA", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{profile.fullName}</strong>
                      {/*
                        The chevron sits on the same line as the words it belongs
                        to, in a ring that reads as a control. Floating loose at the
                        end of the cell it looked like decoration, and it was
                        vertically centred against the whole row rather than against
                        the label that explains it.

                        A span, not a button: the row itself is the toggle, and a
                        nested button inside a clickable row means two things to
                        press for one action.
                      */}
                      <span className="speaker-crm-disclosure">
                        {isExpanded ? "Hide details" : "Show details"}
                        <span className="speaker-crm-disclosure__ring" aria-hidden="true">
                          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </span>
                      </span>

                    </div>
                  </div>
                  </td>
                  <td><span style={{ display: "block", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{profile.organization || "—"}</span><span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{profile.professionalTitle || "No title"}</span></td>
                  <td><span style={{ display: "inline-flex", padding: ".32rem .48rem", borderRadius: "999px", color: state.color, background: state.background, border: `1px solid ${state.border}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, whiteSpace: "nowrap" }}>{state.label}</span></td>
                  <td><span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", color: portraitPublicationColor(profile.portraitPublicationStatus), fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{profile.portraitPublicationStatus === "published" && <CheckCircle2 size={14} strokeWidth={2.4} aria-hidden="true" />}{portraitPublicationLabel(profile.portraitPublicationStatus)}</span></td>
                  <td><span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", whiteSpace: "nowrap" }}>{humanizeDate(profile.createdAt)}</span></td>
                  <td>
                    {/*
                      One trigger, same component as the other CRM tables. The
                      menu carries no name header: the row it belongs to is
                      already the answer to "whose actions are these".
                    */}
                    <div style={{ display: "flex", justifyContent: "flex-start" }} className="speaker-crm-actions" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()} onContextMenu={event => event.stopPropagation()}>
                      <DataTableActions
                        align="start"
                        subject={profile.fullName}
                        actions={[
                          {
                            key: "lifecycle",
                            label: lifecycleActionLabel,
                            icon: profile.status === "approved" ? <PauseCircle size={13} aria-hidden="true" /> : <BadgeCheck size={13} aria-hidden="true" />,
                            disabled: isMutating,
                            onSelect: () => profile.status === "approved"
                              ? setSpeakerToSuspend({ id: profile.id, name: profile.fullName })
                              : openPortraitPublication(profile, assignments),
                          },
                          ...(profile.status === "approved" && profile.portraitPublicationStatus !== "published"
                            ? [{
                                key: "retry-portrait",
                                label: "Retry public portrait",
                                icon: <BadgeCheck size={13} aria-hidden="true" />,
                                disabled: isMutating,
                                onSelect: () => openPortraitPublication(profile, assignments, true),
                              }]
                            : []),
                          {
                            key: "resend",
                            label: "Resend setup invitation",
                            icon: <Send size={13} aria-hidden="true" />,
                            disabled: isMutating,
                            onSelect: () => resendInvitation.mutate({ speakerId: profile.id }),
                          },
                          {
                            key: "assign",
                            label: "Assign to event",
                            icon: <CalendarPlus size={13} aria-hidden="true" />,
                            onSelect: () => { setAssignSpeakerId(profile.id); setAssignEventSlug(null); setAssignOpen(true); },
                          },
                          /*
                            Last in the list and destructive-styled, because it is
                            the only entry here that removes something. It takes the
                            speaker role away; the person keeps their member account.
                          */
                          {
                            key: "remove",
                            label: "Remove Speaker Access",
                            icon: <Trash2 size={13} aria-hidden="true" />,
                            tone: "danger" as const,
                            disabled: removeSpeaker.isPending,
                            onSelect: () => setPendingRemoval({ id: profile.id, name: profile.fullName, kind: "remove" }),
                          },
                        ]}
                      />
                    </div>
                  </td>
                  </tr>
                </ContextMenuTrigger>
                {/* colSpan tracks the header: 8 columns became 9 when the portrait got its own. */}
                {isExpanded && <tr className="speaker-crm-detail-row"><td colSpan={7}><div className="speaker-crm-detail-reveal"><div className="speaker-crm-detail-reveal__inner">
                <div className="speaker-crm-detail-grid" style={{ padding: "1.15rem", display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: "1rem", borderRadius: ".7rem", border: "1px solid rgba(255,255,255,.07)", background: "rgba(0,0,0,.14)" }}>
                  {/*
                    Three questions an administrator actually has about a speaker:
                    how do I reach them, what are they here to talk about, and does
                    this record need something from me. The old panel answered a
                    fourth nobody asked — "Lifecycle" — by stacking a creation date,
                    an approval state and an email-delivery status with equal weight
                    and no way to act on any of them.
                  */}
                  <div>
                    <p className="speaker-crm-detail__label">How to reach them</p>
                    <p className="speaker-crm-detail__value">{profile.email ?? "No email on file"}</p>
                    <p className="speaker-crm-detail__value">{formatPhoneForDisplay(profile.phone) ?? "No phone number"}</p>
                    {profile.linkedinUrl
                      ? <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" className="speaker-crm-detail__link"><Link2 size={12} aria-hidden="true" /> LinkedIn profile</a>
                      : <p className="speaker-crm-detail__value speaker-crm-detail__value--muted">No LinkedIn profile</p>}
                  </div>

                  <div>
                    <p className="speaker-crm-detail__label">What they want to speak about</p>
                    <p className="speaker-crm-detail__value">{profile.proposedTopic || "No topic proposed yet"}</p>
                  </div>

                  <div>
                    <p className="speaker-crm-detail__label">Where this record stands</p>
                    {/*
                      The state, then the one thing that moves it forward, then the
                      dates — in that order, because the first two are decisions and
                      the third is provenance.
                    */}
                    <p className="speaker-crm-detail__value" style={{ color: state.color, fontWeight: 700 }}>
                      {profile.status === "approved" ? "Approved — can sign in to the portal" : profile.status === "suspended" ? "Suspended — portal access revoked" : "Waiting on your approval"}
                    </p>
                    {profile.status === "pending" && (
                      <button
                        type="button"
                        className="speaker-crm-detail__cta"
                        disabled={isMutating}
                        onClick={() => openPortraitPublication(profile, assignments)}
                      >
                        <BadgeCheck size={13} aria-hidden="true" /> Approve this speaker
                      </button>
                    )}
                    <p className="speaker-crm-detail__value speaker-crm-detail__value--muted">
                      Added {humanizeDate(profile.createdAt)}
                      {profile.status === "approved" && profile.approvedAt ? ` · approved ${humanizeDate(profile.approvedAt)}` : ""}
                    </p>
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <p className="speaker-crm-detail__label">{assignments.length === 1 ? "Assigned event" : "Assigned events"}</p>
                    {assignments.length === 0 ? (
                      <p className="speaker-crm-detail__value speaker-crm-detail__value--muted">Not booked for an event yet.</p>
                    ) : (
                      <div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap", marginTop: ".4rem" }}>
                        {assignments.map(assignment => {
                          const talkTitle = assignedTalkTitleBySlug.get(assignment.eventSlug) ?? "Talk title unavailable";
                          return (
                            /* Clickable through to the event in the Events
                               workspace: the assignment names a talk, and the next
                               question is always about that talk. */
                            /*
                              Clicking shows the flyer when there is one, and goes
                              to the event when there is not. The flyer is the thing
                              an administrator is usually checking on a speaker
                              record; the event page is the fallback for a booking
                              with nothing to look at yet.
                            */
                            <button
                              key={assignment.id}
                              type="button"
                              data-assigned-talk-pill="true"
                              title={assignment.promotionalGraphicUrl
                                ? `View the flyer for ${talkTitle}`
                                : onOpenEvent ? `Open ${talkTitle} in Events` : talkTitle}
                              disabled={!assignment.promotionalGraphicUrl && !onOpenEvent}
                              onClick={() => {
                                if (assignment.promotionalGraphicUrl) {
                                  setFlyerPreview({ url: assignment.promotionalGraphicUrl, title: talkTitle });
                                  return;
                                }
                                onOpenEvent?.(assignment.eventSlug);
                              }}
                              className="speaker-crm-talk-pill"
                            >
                              {talkTitle}
                              {assignment.promotionalGraphicUrl
                                ? <><span className="speaker-crm-talk-pill__flag">Flyer</span><ImageIcon size={12} aria-hidden="true" /></>
                                : onOpenEvent && <ArrowUpRight size={12} aria-hidden="true" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/*
                  Upcoming engagements as a table, spanning the panel below the three
                  columns above.

                  The talk pills above list every assignment a speaker has ever had,
                  which answers "what have they done". This answers the different
                  question an organizer actually arrives with: what is coming, and
                  when. Capped at five, with the whole list one click away — a speaker
                  with twenty engagements would otherwise turn the panel into a
                  scrolling log.
                */}
                {(() => {
                  const upcoming = upcomingAssignments(assignments);
                  if (upcoming.length === 0 && !onViewSpeakerEvents) return null;

                  return (
                    <div className="speaker-crm-upcoming">
                      <div className="speaker-crm-upcoming__head">
                        <p>Upcoming events</p>
                        {onViewSpeakerEvents && (
                          <button
                            type="button"
                            onClick={() => onViewSpeakerEvents(profile.fullName)}
                            title={`Open Events filtered to ${profile.fullName}`}
                          >
                            View all speaker events <ArrowUpRight size={12} aria-hidden="true" />
                          </button>
                        )}
                      </div>

                      {upcoming.length === 0 ? (
                        <p className="speaker-crm-upcoming__empty">
                          No upcoming engagements. {assignments.length > 0 ? "Their assigned talks have all taken place." : "Assign them to an event to get started."}
                        </p>
                      ) : (
                        <table className="speaker-crm-upcoming__table">
                          <caption className="sr-only">Next {upcoming.length} upcoming engagements for {profile.fullName}</caption>
                          <thead>
                            <tr>
                              <th scope="col"><CrmColumnHeading label="Event" /></th>
                              <th scope="col"><CrmColumnHeading label="Date" /></th>
                              <th scope="col"><CrmColumnHeading label="Time" /></th>
                            </tr>
                          </thead>
                          <tbody>
                            {upcoming.map(({ assignment, timing }) => {
                              const title = assignedTalkTitleBySlug.get(assignment.eventSlug) ?? assignment.eventSlug;
                              return (
                                <tr key={assignment.id}>
                                  <td>
                                    {/*
                                      A button only where it can go somewhere. Outside
                                      the CRM shell there is no Events tab to open, and
                                      a control that does nothing is worse than text.
                                    */}
                                    {onOpenEvent ? (
                                      <button type="button" onClick={() => onOpenEvent(assignment.eventSlug)} title={`Open ${title} in Events`}>
                                        {title}
                                      </button>
                                    ) : title}
                                  </td>
                                  <td>{timing.displayDate}</td>
                                  <td>{timing.time}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })()}
                </div></div></td></tr>}
                  <ContextMenuContent>
                    <ContextMenuLabel>{profile.fullName}</ContextMenuLabel>
                    <ContextMenuSeparator />
                    <ContextMenuItem onSelect={() => { setAssignSpeakerId(profile.id); setAssignEventSlug(null); setAssignOpen(true); }}><CalendarPlus size={14} /> Assign to event</ContextMenuItem>
                    {(() => {
                      const featured = featuredFor(profile.memberAccountId, profile.fullName ?? "");
                      return (
                        <ContextMenuItem
                          disabled={setFeatured.isPending || unsetFeatured.isPending}
                          onSelect={() => {
                            if (featured) {
                              unsetFeatured.mutate({ id: featured.id });
                              return;
                            }
                            setFeatured.mutate({
                              memberAccountId: profile.memberAccountId ?? null,
                              /* The first assignment gives the card its engagement. */
                              eventSlug: assignments[0]?.eventSlug ?? null,
                              displayName: profile.fullName ?? "",
                              organization: profile.organization ?? null,
                              headline: profile.professionalTitle ?? null,
                              bio: profile.bio ?? null,
                              photoUrl: profile.publicPortraitUrl ?? null,
                              linkedinUrl: profile.linkedinUrl ?? null,
                              websiteUrl: null,
                              monogram: null,
                              accentColor: null,
                              displayOrder: 0,
                              /* Published straight away: the action reads as "feature this person". */
                              published: true,
                            });
                          }}
                        >
                          <Sparkles size={14} />
                          {featured ? "Remove from Featured Speakers" : "Set as Featured Speaker"}
                        </ContextMenuItem>
                      );
                    })()}
                    <ContextMenuItem disabled={isMutating} onSelect={() => profile.status === "approved" ? setSpeakerToSuspend({ id: profile.id, name: profile.fullName }) : openPortraitPublication(profile, assignments)}>{profile.status === "approved" ? <PauseCircle size={14} /> : <BadgeCheck size={14} />}{lifecycleActionLabel}</ContextMenuItem>
                    {profile.status === "approved" && profile.portraitPublicationStatus !== "published" && <ContextMenuItem disabled={isMutating} onSelect={() => openPortraitPublication(profile, assignments, true)}><BadgeCheck size={14} /> Retry public portrait</ContextMenuItem>}
                    {profile.status === "approved" && hasPortraitSource(profile) && (
                      <ContextMenuItem disabled={isMutating} onSelect={() => publishPhotoAsIs(profile)}>
                        <BadgeCheck size={14} /> {profile.portraitPublicationStatus === "published" ? "Replace portrait with current photo" : "Use current photo as portrait"}
                      </ContextMenuItem>
                    )}
                    <ContextMenuItem disabled={isMutating} onSelect={() => resendInvitation.mutate({ speakerId: profile.id })}><Send size={14} /> Resend setup invitation</ContextMenuItem>
                  </ContextMenuContent>
              </ContextMenu>;
            })}
                </tbody>
              </table>
              {rows.length === 0 && <div style={{ padding: "2.25rem 1rem", textAlign: "center", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}><UsersRound size={21} style={{ margin: "0 auto .55rem", color: "#64748B" }} />No speaker profiles match this search.</div>}
            </div>
        </>
      </CrmTablePanel>
      {/*
        One dialog for both removals, because they are the same act on the same
        row and differ only in what the row currently means: a request nobody has
        accepted, or a role somebody holds. The wording changes; the outcome —
        the speaker record goes, the member stays — does not.
      */}
      <AlertDialog open={Boolean(pendingRemoval)} onOpenChange={open => { if (!open) setPendingRemoval(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingRemoval?.kind === "deny" ? "Deny this speaker request?" : "Remove the speaker role?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval?.kind === "deny"
                ? `${pendingRemoval?.name}'s request will be deleted and will leave the pending list. Their member account, directory listing and history are not affected, and they can be added as a speaker later.`
                : `${pendingRemoval?.name} will stop being a speaker: the speaker record, Speaker Workspace access and any event assignments are deleted. Their member account, directory listing and registration history remain, and the role can be added again later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={denyRequest.isPending || removeSpeaker.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={denyRequest.isPending || removeSpeaker.isPending}
              onClick={() => {
                if (!pendingRemoval) return;
                if (pendingRemoval.kind === "deny") denyRequest.mutate({ speakerId: pendingRemoval.id });
                else removeSpeaker.mutate({ speakerId: pendingRemoval.id });
                setPendingRemoval(null);
              }}
            >
              {pendingRemoval?.kind === "deny" ? "Deny request" : "Remove Speaker Access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={Boolean(speakerToSuspend)} onOpenChange={open => { if (!open) setSpeakerToSuspend(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Speaker Access?</AlertDialogTitle>
            <AlertDialogDescription>{speakerToSuspend ? `${speakerToSuspend.name} will keep their membership, billing status, speaker record, and assigned engagements, but will no longer be able to access the Speaker Workspace until an administrator restores access.` : "The speaker record will remain intact."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={suspendSpeaker.isPending}>Keep access</AlertDialogCancel>
            <AlertDialogAction disabled={suspendSpeaker.isPending} onClick={() => { if (speakerToSuspend) suspendSpeaker.mutate({ speakerId: speakerToSuspend.id }); }}>Remove Speaker Access</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={Boolean(speakerToPublish)} onOpenChange={open => { if (!open && !approveSpeaker.isPending && !retryPortraitPublication.isPending) { setSpeakerToPublish(null); setPublicationError(null); } }}>
        <AlertDialogContent style={{ maxWidth: 720, maxHeight: "90vh", overflowY: "auto", background: "#101727", borderColor: "rgba(0,212,255,.28)", color: "#F8FAFC" }}>
          <AlertDialogHeader>
            <AlertDialogTitle>{speakerToPublish?.isRetry ? "Retry public portrait" : "Grant Speaker Workspace access"}</AlertDialogTitle>
             <AlertDialogDescription style={{ color: "#AAB5C8", lineHeight: 1.55 }}>{speakerToPublish?.isRetry
              ? speakerToPublish.sourceKind === "member_permission"
                 ? "Reprocess the generated portrait from the member’s consented profile photo, or provide a replacement source photo, without changing Speaker Workspace access or sending another approval notification."
                : "Reprocess the public portrait without changing Speaker Workspace access or sending another approval notification."
              : "This keeps the person’s membership entitlement and billing status unchanged. It approves the speaker profile, links it to the verified account, and unlocks the Speaker Workspace. A successful portrait workflow also creates a public card sorted by the earliest assigned event."}</AlertDialogDescription>
          </AlertDialogHeader>
          <form onSubmit={submitPortraitPublication}>
            {!speakerToPublish?.isRetry && <fieldset style={{ margin: "1rem 0", padding: ".9rem", border: "1px solid rgba(255,255,255,.09)", borderRadius: ".75rem" }}>
              <legend style={{ padding: "0 .35rem", color: "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>Speaker Workspace and public roster event assignment</legend>
              <p style={{ margin: "0 0 .7rem", color: "#8390A6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>Choose confirmed event assignments. The speaker can see upcoming assignments in the Speaker Workspace, and the public card is placed by the earliest event date.</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: ".5rem" }}>
                {EVENTS.map(event => <label key={event.slug} style={{ display: "flex", alignItems: "flex-start", gap: ".55rem", padding: ".62rem", border: `1px solid ${publicationEventSlugs.includes(event.slug) ? "rgba(0,212,255,.35)" : "rgba(255,255,255,.08)"}`, background: publicationEventSlugs.includes(event.slug) ? "rgba(0,212,255,.07)" : "rgba(255,255,255,.02)", borderRadius: ".55rem", color: "#C7D0DF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.4, cursor: "pointer" }}><input type="checkbox" checked={publicationEventSlugs.includes(event.slug)} onChange={() => togglePublicationEvent(event.slug)} style={{ marginTop: ".12rem", accentColor: "#00D4FF" }} /><span><strong style={{ color: "#EEF2F7", fontWeight: 600 }}>{event.title}</strong><br /><span style={{ color: "#718096" }}>{event.displayDate}</span></span></label>)}
              </div>
            </fieldset>}
            <fieldset style={{ margin: "1rem 0", padding: ".9rem", border: "1px solid rgba(255,255,255,.09)", borderRadius: ".75rem" }}>
              <legend style={{ padding: "0 .35rem", color: "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>Authorized portrait source</legend>
              <p style={{ margin: "0 0 .7rem", color: "#8390A6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>The editorial process preserves the speaker’s identity while applying a consistent dark professional background and restrained color correction.</p>
              {speakerToPublish?.hasApplicationPortrait && <label style={{ display: "flex", gap: ".5rem", alignItems: "flex-start", marginBottom: ".55rem", color: "#CDD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}><input type="radio" checked={portraitSource === "application"} onChange={() => { setPortraitSource("application"); setPublicationError(null); }} style={{ marginTop: ".14rem", accentColor: "#00D4FF" }} /><span><strong style={{ color: "#EEF2F7" }}>Consented application portrait</strong><br /><span style={{ color: "#8390A6" }}>Use the photo the speaker provided with their application.</span></span></label>}
              <label style={{ display: "flex", gap: ".5rem", alignItems: "flex-start", color: "#CDD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}><input type="radio" checked={portraitSource === "linkedin_image"} onChange={() => { setPortraitSource("linkedin_image"); setPublicationError(null); }} style={{ marginTop: ".14rem", accentColor: "#00D4FF" }} /><span><strong style={{ color: "#EEF2F7" }}>Authorized LinkedIn image</strong><br /><span style={{ color: "#8390A6" }}>Provide a direct public image URL, not the LinkedIn profile-page URL. The original is cached privately for safe retries.</span></span></label>
              {portraitSource === "linkedin_image" && <div style={{ marginTop: ".75rem" }}><label htmlFor="speaker-linkedin-image-url" style={{ display: "block", marginBottom: ".35rem", color: "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>Direct LinkedIn image URL</label><input id="speaker-linkedin-image-url" type="url" value={linkedinImageUrl} onChange={event => { setLinkedinImageUrl(event.target.value); setPublicationError(null); }} placeholder="Enter the direct image URL" maxLength={700} style={fieldInput} /></div>}<label style={{ display: "flex", gap: ".5rem", alignItems: "flex-start", marginTop: ".55rem", color: "#CDD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}><input type="radio" checked={portraitSource === "admin_upload"} onChange={() => { setPortraitSource("admin_upload"); setPublicationError(null); }} style={{ marginTop: ".14rem", accentColor: "#00D4FF" }} /><span><strong style={{ color: "#EEF2F7" }}>Upload a photo</strong><br /><span style={{ color: "#8390A6" }}>Upload a photo file directly instead of providing a public link.</span></span></label>{portraitSource === "admin_upload" && <div style={{ marginTop: ".75rem" }}><label htmlFor="speaker-portrait-upload" style={{ display: "block", marginBottom: ".35rem", color: "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>Photo file (PNG, JPEG, or WebP, up to 1.5 MB)</label><input id="speaker-portrait-upload" type="file" accept="image/png,image/jpeg,image/webp" onChange={handlePortraitFileSelect} style={{ ...fieldInput, padding: ".5rem" }} />{uploadFileName && <p style={{ margin: ".4rem 0 0", color: "#86EFAC", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Selected: {uploadFileName}</p>}</div>}
            </fieldset>
            {publicationError && <p role="alert" style={{ margin: "0 0 1rem", padding: ".65rem .75rem", borderRadius: ".55rem", color: "#FDA4AF", background: "rgba(251,113,133,.08)", border: "1px solid rgba(251,113,133,.28)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>{publicationError}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={approveSpeaker.isPending || retryPortraitPublication.isPending}>Cancel</AlertDialogCancel>
              <button type="submit" disabled={approveSpeaker.isPending || retryPortraitPublication.isPending} style={{ minHeight: 40, display: "inline-flex", alignItems: "center", gap: ".45rem", padding: ".65rem .85rem", border: "1px solid rgba(0,212,255,.48)", borderRadius: ".58rem", background: "linear-gradient(135deg,#007C91,#005563)", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: approveSpeaker.isPending || retryPortraitPublication.isPending ? "wait" : "pointer", opacity: approveSpeaker.isPending || retryPortraitPublication.isPending ? .72 : 1 }}>{approveSpeaker.isPending || retryPortraitPublication.isPending ? <LoaderCircle className="chi-notification-spinner" /> : <BadgeCheck size={14} />}{speakerToPublish?.isRetry ? "Retry portrait" : "Grant access & publish"}</button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
      <style>{`
        .speaker-flyer-lightbox {
          position: fixed;
          inset: 0;
          z-index: 130;
          display: grid;
          place-items: center;
          padding: 2rem 1.5rem;
          background: rgba(4,7,16,.82);
          backdrop-filter: blur(6px);
        }

        .speaker-flyer-lightbox__panel {
          display: flex;
          flex-direction: column;
          gap: .75rem;
          max-width: min(46rem, 100%);
          max-height: 90vh;
        }

        .speaker-flyer-lightbox__bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          color: #F8FAFC;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
        }

        .speaker-flyer-lightbox__panel img {
          min-height: 0;
          max-width: 100%;
          object-fit: contain;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: .75rem;
        }

        .speaker-crm-detail__value--muted { color: #728096; }

        .speaker-crm-detail__link {
          display: inline-flex;
          align-items: center;
          gap: .3rem;
          color: #7DEAFF;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          text-decoration: none;
        }

        .speaker-crm-detail__link:hover { text-decoration: underline; }

        .speaker-crm-talk-pill {
          display: inline-flex;
          align-items: center;
          gap: .4rem;
          padding: .42rem .7rem;
          border: 1px solid rgba(186,231,255,.28);
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(0,212,255,.19), rgba(168,85,247,.18) 48%, rgba(233,30,140,.18));
          color: #F1F7FF;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 650;
          line-height: 1.25;
          cursor: pointer;
        }

        .speaker-crm-talk-pill:disabled { cursor: default; }
        .speaker-crm-talk-pill:hover:not(:disabled) { border-color: rgba(186,231,255,.55); }

        .speaker-crm-talk-pill__flag {
          padding-left: .4rem;
          border-left: 1px solid rgba(255,255,255,.22);
          color: #BBF7D0;
        }

        .speaker-crm-disclosure {
          display: inline-flex;
          align-items: center;
          gap: .4rem;
          margin-top: .15rem;
          color: #7E8CA0;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
        }

        .speaker-crm-disclosure__ring {
          display: inline-grid;
          place-items: center;
          width: 1.45rem;
          height: 1.45rem;
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 999px;
          background: rgba(255,255,255,.05);
          color: #CBD5E1;
          transition: background .16s ease, border-color .16s ease, color .16s ease;
        }

        tr:hover .speaker-crm-disclosure__ring,
        tr:focus-within .speaker-crm-disclosure__ring {
          border-color: rgba(0,212,255,.42);
          background: rgba(0,212,255,.12);
          color: #7DEAFF;
        }

        @media (prefers-reduced-motion: reduce) {
          .speaker-crm-disclosure__ring { transition: none; }
        }

        /* Sized to the 30px portrait and no wider, so the leading column does not
           take a share of the free space a fixed-size avatar has no use for. */
        /* Matches the members table: sized to the portrait, with a real gap to
           its right so the face is not pressed against the name column. */
         .speaker-portrait-cell { width:46px; padding-right:1.1rem !important; }
         .speaker-crm-portrait-wrap { position:relative; width:46px; height:46px; }
         .speaker-crm-portrait-alert { position:absolute; top:-5px; right:-7px; z-index:2; width:20px; height:20px; display:grid; place-items:center; padding:0; border:2px solid #101727; border-radius:50%; background:#EF4444; color:#FFF; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:800; line-height:1; cursor:pointer; box-shadow:0 2px 8px rgba(0,0,0,.32); }
         .speaker-crm-portrait-alert:hover,.speaker-crm-portrait-alert:focus-visible { background:#F87171; outline:2px solid rgba(248,113,113,.5); outline-offset:2px; }

        /* Upcoming engagements, spanning the detail panel under its three columns. */
        .speaker-crm-upcoming { margin-top:.85rem; padding:.85rem; border:1px solid rgba(255,255,255,.07); border-radius:.7rem; background:rgba(0,0,0,.14); }
        .speaker-crm-upcoming__head { display:flex; align-items:center; justify-content:space-between; gap:.75rem; flex-wrap:wrap; margin-bottom:.6rem; }
        .speaker-crm-upcoming__head p { margin:0; color:#CDD5E1; font-family:'Space Grotesk',sans-serif; font-size:var(--chi-body-compact); font-weight:750; letter-spacing:.06em; text-transform:uppercase; }
        .speaker-crm-upcoming__head button { display:inline-flex; align-items:center; gap:.3rem; min-height:32px; padding:0 .55rem; border:1px solid rgba(0,212,255,.3); border-radius:.5rem; background:rgba(0,212,255,.08); color:#7DEAFF; font-family:'Space Grotesk',sans-serif; font-size:var(--chi-body-compact); font-weight:700; cursor:pointer; white-space:nowrap; }
        .speaker-crm-upcoming__head button:hover { border-color:#7DEAFF; }
        .speaker-crm-upcoming__empty { margin:0; color:#7E8CA0; font-family:'Inter',sans-serif; font-size:var(--chi-body-compact); line-height:1.5; }
        .speaker-crm-upcoming__table { width:100%; border-collapse:collapse; }
        .speaker-crm-upcoming__table th,.speaker-crm-upcoming__table td { padding:.45rem .5rem; border-bottom:1px solid rgba(255,255,255,.06); text-align:left; vertical-align:middle; }
        .speaker-crm-upcoming__table th { color:#7E8CA0; font-family:'Space Grotesk',sans-serif; font-size:var(--chi-body-compact); font-weight:750; letter-spacing:.06em; text-transform:uppercase; }
        .speaker-crm-upcoming__table td { color:#CBD5E1; font-family:'Inter',sans-serif; font-size:var(--chi-body-compact); }
        .speaker-crm-upcoming__table tbody tr:last-child td { border-bottom:0; }
        .speaker-crm-upcoming__table td button { padding:0; border:0; background:transparent; color:#E2E8F0; font:inherit; text-align:left; cursor:pointer; }
        .speaker-crm-upcoming__table td button:hover { color:#7DEAFF; text-decoration:underline; }
        .speaker-table tbody tr.speaker-crm-detail-row:hover { background:transparent; }
        /*
          The expanded panel needs a gap above it, not none.

          This was a zero top padding, to pull the detail tight against the row it
          belongs to. It pulled too hard: the panel's own border sat directly on
          the speaker row's bottom edge, so the two boxes shared a line and the
          detail read as part of the row rather than as something opened beneath
          it. Enough separation to see it is its own container, still close
          enough to belong to the speaker above.
        */
        .speaker-table tbody tr.speaker-crm-detail-row td { padding-top:.85rem; padding-bottom:1.1rem; }
        /* The detail grows out of the row above it rather than being there the
           instant it is asked for. Animating grid-template-rows from 0fr means
           the panel's real height is what changes, so the rest of the table is
           pushed down in step with the reveal instead of jumping once. The
           content fades and settles slightly later than the height, which reads
           as the panel opening rather than as text arriving. */
        .speaker-crm-detail-reveal { display:grid; grid-template-rows:0fr; animation:speaker-crm-detail-grow 260ms cubic-bezier(.22,1,.36,1) forwards; }
        .speaker-crm-detail-reveal__inner { overflow:hidden; min-height:0; animation:speaker-crm-detail-settle 260ms cubic-bezier(.22,1,.36,1) forwards; }
        @keyframes speaker-crm-detail-grow { from { grid-template-rows:0fr; } to { grid-template-rows:1fr; } }
        @keyframes speaker-crm-detail-settle { from { opacity:0; transform:translateY(-5px); } to { opacity:1; transform:none; } }
        @media (prefers-reduced-motion: reduce) {
          .speaker-crm-detail-reveal { grid-template-rows:1fr; animation:none; }
          .speaker-crm-detail-reveal__inner { animation:none; opacity:1; transform:none; }
        }
        /* .speaker-create-drawer now lives in index.css. It was declared here, in a
           <style> element that only exists while this view is mounted, so the same
           class on the Events and Members drawers fell back to the sheet default of
           24rem and cramped their content. */
        @media (max-width: 720px) { .speaker-crm-stats, .speaker-crm-form-grid, .speaker-crm-detail-grid { grid-template-columns: minmax(0,1fr) !important; } .speaker-crm-detail-grid > div { grid-column: 1 / -1 !important; } }
      `}</style>
    </div>
  );
}
